<template>
  <div class="p-3 vstack gap-3 bg-light">
    <template v-if="resultOrchestrators.length > 0">
      <div v-if="resultOrchestrators.length != 1" class="p-3 bg-white rounded shadow-sm">
        <h3>{{ t("backend.e2e.voting_round_reports.show.overall_turnout") }}</h3>
        <div class="accent-line mt-2 mb-4"></div>
        <!-- Based on the assumption that if we're calculating a result there has been at least one vote -->
        <TurnoutTable
          v-if="votingRoundReport.status === 'finished' && votingRoundReport.turnout.voted !== 0"
          :turnout="votingRoundReport.turnout"
        />
        <template v-else-if="votingRoundReport.status !== 'finished'">
          {{ t("backend.e2e.voting_round_reports.show.waiting_for_results") }}
        </template>
        <template v-else>
          <AVSpinner size="sm" color="primary-30" />
          {{ t("backend.e2e.voting_round_reports.show.calculating") }}
        </template>
      </div>
      <div v-if="contestsToMap" class="p-3 bg-white rounded shadow-sm">
        <h3>{{ t("backend.e2e.voting_round_reports.show.write_in_mapping") }}</h3>
        <div class="accent-line mt-2 mb-4"></div>
        <p>{{ t("backend.e2e.voting_round_reports.show.write_in_mapping_description") }}</p>
        <button v-if="contestsToMap > 1" class="btn btn-secondary" @click="skipMapping">
          {{ t("backend.e2e.voting_round_reports.show.mapping_btn") }}
        </button>
      </div>
      <ResultOrchestrator
        v-for="resultOrchestrator in resultOrchestrators"
        :key="`result_orchestrator_${resultOrchestrator.id}`"
        :result-orchestrator="resultOrchestrator"
        :trustees="trustees"
        :voting-round-report="votingRoundReport"
        :contest="contestFor(resultOrchestrator.contestReference)"
        :threshold="orchestrator.threshold"
      />
    </template>
    <div v-else class="p-3 bg-white rounded shadow-sm">
      <template v-if="votingRoundReport?.status === 'starting'">
        <h3>{{ t("backend.e2e.voting_round_reports.show.ceremony_starting") }}</h3>
        <div class="accent-line mt-2 mb-4"></div>
      </template>
      <div class="text-center">
        <AVSpinner size="md" style="width: 3rem; height: 3rem" color="dark" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, onUnmounted } from "vue";
import { CeremonyChannelSubscription } from "@/channels/CeremonyChannel";
import ResultOrchestrator from "@/components/backend/ResultOrchestrator.vue";
import TurnoutTable from "@/components/backend/live/TurnoutTable.vue";
import apiClient from "@/entrypoints/frontend/apiClient";
import { useI18n } from "vue-i18n";
import type {
  ElectaTrustee,
  ConferenceContest,
  ConferenceVotingRoundReport,
  E2E,
  CeremonyData,
  PropType
} from "@/types";

const { t } = useI18n();

const props = defineProps({
  initData: {
    type: Object as PropType<CeremonyData>,
    default: null,
  },
  orchestratorId: {
    type: Number,
    required: true,
  },
  reportId: {
    type: Number,
    required: true,
  },
  refreshOnFinish: {
    type: Boolean,
    default: true
  },
});

const subscription = ref<CeremonyChannelSubscription>(null);

const orchestrator = ref<E2E.ResultOrchestrator>(null);

const resultOrchestrators = ref<Array<E2E.ResultOrchestrator>>([]);

const contests = ref<Array<ConferenceContest>>([]);

const votingRoundReports = ref<Array<ConferenceVotingRoundReport>>([]);

const votingRoundReport = computed(() => votingRoundReports.value.find((report: ConferenceVotingRoundReport) => report.id === props.reportId));

const trustees = ref<Array<ElectaTrustee>>([]);

const contestsToMap = computed(() => resultOrchestrators.value.filter((ro: E2E.ResultOrchestrator) => ro.status === "mapping").length);

const applyFullUpdate = (state) => {
  contests.value = state.contests || contests.value;
  orchestrator.value = state.orchestrator || orchestrator.value;
  votingRoundReports.value = Array.isArray(state.votingRoundReports) ? state.votingRoundReports : [state.votingRoundReports];
  resultOrchestrators.value = votingRoundReport.value?.resultOrchestrators || state.resultOrchestrators || [];
  trustees.value = state.trustees || trustees.value;
};

const handleReceived = (data) => {
  switch (data.type) {
    case "full_update":
      applyFullUpdate(data.state);
      break;
    default:
      subscription.value?.fetchStatus();
  }
};

const contestFor = (contestReference: string) => contests.value.find((c: ConferenceContest) => c.reference === contestReference);

const skipMapping = () => {
  const postRequests = resultOrchestrators.value.map((ro: E2E.ResultOrchestrator) => {
    if (ro.status === "mapping") {
      const url = `${window.location.href.split("/voting_rounds/")[0]}/e2e/result_orchestrators/${ro.id}/create_mapping`;
      const filteredMapping: Record<string, Record<string, string>> = {};
      Object.entries(ro.writeIns).forEach(([reference]) => filteredMapping[reference] = {});
      return apiClient.post(url, { write_in_mapping: filteredMapping });
    }
  });
  Promise.all(postRequests).catch(error => console.error(error));
};

watch(votingRoundReport, (newVal, oldVal) => {
  if (props.refreshOnFinish && newVal.status === "finished" && oldVal?.status === "aggregating") {
    const resultControlButtons = document.querySelector("#result-controls");
    const editVrrButton = document.querySelector("#edit-voting-round-report");
    if (resultControlButtons) {
      (resultControlButtons as HTMLElement).classList.remove("visually-hidden");
    }
    if (editVrrButton) {
      (editVrrButton as HTMLElement).classList.remove("disabled");
    }
  }
});

onMounted(() => {
  if (props.initData) {
    applyFullUpdate(props.initData);
  }

  subscription.value = new CeremonyChannelSubscription(props.orchestratorId, {
    received: handleReceived,
  }, props.reportId);
});

onUnmounted(() => subscription.value?.unsubscribe());
</script>
